import React, { useState, useEffect } from "react";
import Tree from "react-d3-tree";
import "./styles.css";

// Function to shorten the string
const shortenString = (str) => {
  if (str.length <= 8) {
    return str; // If the string is too short, just return it as is
  }
  return `${str.slice(2, 6)}...${str.slice(-4)}`;
};

const AncestryTree = ({ data }) => {
  const [treeData, setTreeData] = useState(null);

  useEffect(() => {
    const buildTree = (data) => {
      // Map addresses to node format
      const addressMap = new Map();

      // The root node
      addressMap.set("0x1", {
        name: shortenString("0x1"),
        color: "gray",
        children: [],
        in_set: false,
      });

      // Helper function to get or create a node
      const getOrCreateNode = (address, user) => {
        if (!addressMap.has(address)) {
          addressMap.set(address, {
            name: shortenString(address),
            user: user,
            children: [],
          });
        }
        return addressMap.get(address);
      };

      // Process each node and its ancestry
      data.forEach((obj) => {
        let currentNode = getOrCreateNode(obj.address, obj.user);
        if (obj.ancestry.length === 0) {
          currentNode.strikeColor = "red";
        }
        currentNode.color = obj.in_set ? "green" : "gray";
        currentNode.bid = obj.bid;

        // Process the ancestry from the oldest ancestor to the immediate parent
        for (let i = obj.ancestry.length - 1; i >= 0; i--) {
          const parentAddress = obj.ancestry[i];
          const parent = data.find((each) => each.address === parentAddress);
          const parentNode = getOrCreateNode(parentAddress, parent?.user);

          // Avoid circular references by checking if currentNode is already a child of parentNode
          if (!parentNode.children.includes(currentNode)) {
            parentNode.children.push(currentNode);
          }

          // Move up the tree
          currentNode = parentNode;
        }

        // Attach the highest ancestor to the root if not already attached
        const root = addressMap.get("0x1");
        if (!root.children.includes(currentNode) && currentNode !== root) {
          root.children.push(currentNode);
        }
      });

      return addressMap.get("0x1");
    };

    const dataTree = buildTree(data);
    setTreeData(dataTree);
  }, [data]);

  if (!treeData) return <div>Loading...</div>;

  // Calculate translate values to center the root node
  const containerHeight = window.innerHeight;
  const containerWidth = window.innerWidth;
  const translate = { x: containerWidth / 4.5, y: containerHeight / 20 }; // Adjust y value to move root node up

  return (
    <>
      <div className="w-full h-screen bg-gray-100 p-4 flex justify-center items-center">
        <Tree
          data={treeData}
          orientation="vertical"
          pathFunc="diagonal"
          rootNodeClassName="node__root"
          branchNodeClassName="node__branch"
          leafNodeClassName="node__leaf"
          translate={translate}
          separation={{ siblings: 2, nonSiblings: 2 }}
          zoom={0.5}
          nodeSize={{ x: 200, y: 200 }}
          renderCustomNodeElement={(rd3tProps) =>
            renderRectSvgNode({ ...rd3tProps })
          }
        />
      </div>
    </>
  );
};

// Custom node element renderer
const renderRectSvgNode = ({ nodeDatum, toggleNode }) => (
  <g>
    <rect
      width="200"
      height="90"
      x="-100"
      y="-40" // Adjust y to center the rect correctly
      fill={nodeDatum.color}
      onClick={toggleNode}
    />
    <text
      fill={nodeDatum.strikeColor ?? "white"}
      stroke={nodeDatum.strikeColor ?? "white"}
      strokeWidth="0.5"
      x="0"
      y="-40" // Adjust y to position the text inside the rect
      textAnchor="middle"
      alignmentBaseline="middle"
    >
      <tspan x="0" dy="1.4em">
        {nodeDatum.name}
      </tspan>
      {nodeDatum.user && (
        <tspan x="0" dy="1.4em">
          {nodeDatum.user}
        </tspan>
      )}
      <tspan x="0" dy="1.6em">
        {nodeDatum.bid ? "Bid: " + nodeDatum.bid : ""}
      </tspan>
    </text>
  </g>
);

export default AncestryTree;
